/**
 * Pure helpers that patch the cached whole-project shape (`ProjectDetailsResponse`)
 * after a v2 per-entity mutation succeeds, so a panel using the new granular
 * `/v2` hooks doesn't need a full whole-project refetch to see its own change -
 * and every other part of the app still reading `project.characters`/etc. from
 * the shared React Query/MobX cache stays in sync too.
 *
 * Kept dependency-free (no react-query/mobx) so these are unit-testable without
 * mocking the cache layer. `hooks/useProjectCachePatch.ts` wires these into the
 * actual React Query + MobX dual-write.
 */
import type { ProjectDetailsProject, ProjectDetailsResponse } from "@/types/project";
import type { V2EntityKind } from "@/services/entityOpsV2";

/** Must match services/projectDetails.ts's PROJECT_STORE_VERSIONS_KEY exactly (project_store.py's
 * VERSIONS_KEY). Inlined rather than imported so this module has zero runtime cross-file
 * imports - same "dependency-free, unit-testable without mocking anything" design as
 * services/entityOpsV2.ts, and it lets e2e/projectCachePatchUnit.ts run under plain `npx tsx`
 * without needing the app's `@/*` path-alias resolution (only wired up for Vite/tsc/Playwright,
 * not bare tsx). */
const PROJECT_STORE_VERSIONS_KEY = "__project_store_versions";

/** project_store.py's META_SK - the fixed (non-prefixed) key META's OCC baseline is
 * stored under in __project_store_versions, alongside the CHAR#/LOC#/SHOT# entries. */
const META_SK = "META";

type ProjectEntityListKey = "characters" | "locations" | "storyboard-shots";

const ENTITY_LIST_KEY_BY_KIND: Record<V2EntityKind, ProjectEntityListKey> = {
  character: "characters",
  location: "locations",
  shot: "storyboard-shots"
};

/** Mirrors project_store.py's SK prefixes (CHAR#/LOC#/SHOT#) - the key each entity's
 * OCC baseline is stored under in `__project_store_versions`. */
const SK_PREFIX_BY_KIND: Record<V2EntityKind, string> = {
  character: "CHAR#",
  location: "LOC#",
  shot: "SHOT#"
};

interface EntityWithId {
  readonly id: string;
}

/** `{version, hash}` per SK - see project_store.py's VERSIONS_KEY. Only `version` is
 * ever read back by these helpers/the v2 endpoints; `hash` is exclusively consumed by
 * the legacy whole-project POST's conflict check and is preserved as-is (or left absent)
 * rather than recomputed, since matching Python's hash byte-for-byte from the frontend
 * isn't needed for correctness - see the Phase 1 log entry in PROJECT_STORE_MIGRATION_LOG.md
 * for why a stale/absent hash there is safe. */
interface VersionEntry {
  readonly version: number;
  readonly hash?: string;
  readonly [key: string]: unknown;
}

type VersionsMap = Readonly<Record<string, VersionEntry>>;

function skFor(kind: V2EntityKind, entityId: string): string {
  return `${SK_PREFIX_BY_KIND[kind]}${entityId}`;
}

function versionsMapOf(project: ProjectDetailsProject): VersionsMap {
  const raw = project[PROJECT_STORE_VERSIONS_KEY];
  return typeof raw === "object" && raw !== null ? (raw as VersionsMap) : {};
}

function entityListKeyFor(kind: V2EntityKind): ProjectEntityListKey {
  return ENTITY_LIST_KEY_BY_KIND[kind];
}

function entityListFor(project: ProjectDetailsProject, kind: V2EntityKind): readonly EntityWithId[] {
  const list = project[entityListKeyFor(kind)];
  return Array.isArray(list) ? (list as readonly EntityWithId[]) : [];
}

function withProject(details: ProjectDetailsResponse, project: ProjectDetailsProject): ProjectDetailsResponse {
  return { ...details, project };
}

function withList(details: ProjectDetailsResponse, kind: V2EntityKind, nextList: readonly EntityWithId[]): ProjectDetailsResponse {
  return withProject(details, { ...details.project, [entityListKeyFor(kind)]: nextList });
}

/**
 * Bump one entity's baseline `version` (leaving whatever `hash` was already
 * there, if any, untouched) after a v2 write returns its fresh version -
 * without this, the *next* v2 write (delete/reorder/update) on the same
 * entity would read a stale `expected_version` from the cache and 409
 * against the user's own just-completed write.
 */
function withEntityVersion(details: ProjectDetailsResponse, kind: V2EntityKind, entityId: string, freshVersion: number): ProjectDetailsResponse {
  const versions = versionsMapOf(details.project);
  const sk = skFor(kind, entityId);
  const nextEntry: VersionEntry = { ...versions[sk], version: freshVersion };
  return withProject(details, { ...details.project, [PROJECT_STORE_VERSIONS_KEY]: { ...versions, [sk]: nextEntry } });
}

/** Drop one entity's baseline entry after a v2 delete - it no longer exists. */
function withoutEntityVersion(details: ProjectDetailsResponse, kind: V2EntityKind, entityId: string): ProjectDetailsResponse {
  const versions = versionsMapOf(details.project);
  const sk = skFor(kind, entityId);
  if (!(sk in versions)) {
    return details;
  }

  const { [sk]: _removed, ...rest } = versions;
  return withProject(details, { ...details.project, [PROJECT_STORE_VERSIONS_KEY]: rest });
}

/** Read the cached `expected_version` baseline for one entity - lets a delete/reorder/update
 * call site build its v2 request body without an extra GET round-trip, since the whole-project
 * response already carries every entity's version in `__project_store_versions`. `undefined` if
 * the project cache holds no baseline for this entity yet (e.g. pre-migration project or a stale
 * cache) - callers should treat that as "fetch a fresh baseline via useEntityV2 first". */
export function getEntityVersion(details: ProjectDetailsResponse | null | undefined, kind: V2EntityKind, entityId: string): number | undefined {
  if (!details?.project) {
    return undefined;
  }

  return versionsMapOf(details.project)[skFor(kind, entityId)]?.version;
}

/** Same as {@link getEntityVersion}, for the singleton META record. */
export function getMetaVersion(details: ProjectDetailsResponse | null | undefined): number | undefined {
  if (!details?.project) {
    return undefined;
  }

  return versionsMapOf(details.project)[META_SK]?.version;
}

/**
 * Insert or replace one entity's data after a v2 create/update. A new id is
 * appended to the end of its list (matches where project_store.py's fractional
 * indexing places a brand-new entity today); an existing id is replaced in
 * place, keeping its current array position untouched - a plain field edit
 * never moves anything, only `patchEntityMoved` does. Also refreshes this
 * entity's cached OCC baseline to the version the v2 write actually returned.
 */
export function patchEntityUpsert<TData extends EntityWithId>(
  details: ProjectDetailsResponse,
  kind: V2EntityKind,
  entityId: string,
  data: TData,
  freshVersion: number
): ProjectDetailsResponse {
  const list = entityListFor(details.project, kind);
  const index = list.findIndex((item) => item.id === entityId);
  const nextList = index === -1 ? [...list, data] : list.map((item, i) => (i === index ? data : item));
  return withEntityVersion(withList(details, kind, nextList), kind, entityId, freshVersion);
}

/** Drop one entity after a v2 delete (data + its OCC baseline). No-op if the id was already absent. */
export function patchEntityRemoved(details: ProjectDetailsResponse, kind: V2EntityKind, entityId: string): ProjectDetailsResponse {
  const list = entityListFor(details.project, kind);
  const nextList = list.filter((item) => item.id !== entityId);
  if (nextList.length === list.length) {
    return details;
  }

  return withoutEntityVersion(withList(details, kind, nextList), kind, entityId);
}

/**
 * Move one entity to a new array index after a v2 reorder. The backend's
 * fractional `order` value is deliberately not used here: the whole-project
 * shape has never exposed `order` to the frontend (`assemble_project_dict`
 * only uses it server-side to sort the list, then drops it from each entity's
 * data), so the caller - a drag-and-drop or move up/down action - already
 * knows the target array index, which is all a plain list splice needs. Also
 * refreshes this entity's cached OCC baseline (a reorder still bumps `version`
 * server-side even though `data` itself is untouched).
 */
export function patchEntityMoved(
  details: ProjectDetailsResponse,
  kind: V2EntityKind,
  entityId: string,
  toIndex: number,
  freshVersion: number
): ProjectDetailsResponse {
  const list = entityListFor(details.project, kind);
  const fromIndex = list.findIndex((item) => item.id === entityId);
  if (fromIndex === -1) {
    return details;
  }

  const nextList = [...list];
  const [moved] = nextList.splice(fromIndex, 1);
  if (!moved) {
    return details;
  }

  const clampedIndex = Math.max(0, Math.min(toIndex, nextList.length));
  nextList.splice(clampedIndex, 0, moved);
  return withEntityVersion(withList(details, kind, nextList), kind, entityId, freshVersion);
}

/** Shallow-merge partial META fields after a v2 project-meta update, and refresh META's
 * cached OCC baseline (mirrors {@link patchEntityUpsert}'s freshVersion handling). */
export function patchProjectMetaMerged<TData extends Record<string, unknown>>(
  details: ProjectDetailsResponse,
  partialData: Partial<TData>,
  freshVersion: number
): ProjectDetailsResponse {
  const versions = versionsMapOf(details.project);
  const nextEntry: VersionEntry = { ...versions[META_SK], version: freshVersion };
  return withProject(details, {
    ...details.project,
    ...partialData,
    [PROJECT_STORE_VERSIONS_KEY]: { ...versions, [META_SK]: nextEntry }
  });
}
